import { useState } from "react";
import { Check } from "lucide-react";
import { KnockDoor, KnockPartial, KnockFull } from "../../components/BrandIcons";
import { PHASES } from "./constants";

// Image that quietly retries (with a cache-busting query + gentle backoff) while a background
// render is still being produced. It never permanently hides — a slow render simply keeps
// polling — and calls onSlow once it has taken noticeably longer than usual so the caller can
// show a reassuring message.
export function RetryImage({ src, alt, className, testid, maxTries = 60, interval = 4000, onLoad, onSlow }) {
  const [tries, setTries] = useState(0);
  if (!src) return null;
  const bust = tries ? `${src}${src.includes("?") ? "&" : "?"}r=${tries}` : src;
  return (
    <img src={bust} alt={alt} className={className} data-testid={testid}
      onLoad={onLoad}
      onError={() => {
        const t = tries + 1;
        if (onSlow && t === 12) onSlow();
        if (t <= maxTries) {
          const wait = t > 20 ? Math.min(interval * 3, 15000) : interval;
          setTimeout(() => setTries(t), wait);
        }
      }} />
  );
}

export function Section({ title, eyebrow, children }) {
  return (
    <section className="px-6 md:px-12 lg:px-16 py-12">
      <div className="label-eyebrow mb-4">{eyebrow}</div>
      <h2 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">{title}</h2>
      <div className="mt-12">{children}</div>
    </section>
  );
}

// Clickable "?" that reveals a plain-English explanation of a question.
export function InfoTip({ text, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex shrink-0">
      <button type="button" data-testid={testid} aria-label="What does this mean?"
        onClick={() => setOpen(o => !o)} onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="w-5 h-5 rounded-full border border-[#C79A3B] text-[#C79A3B] text-[11px] font-bold leading-none flex items-center justify-center hover:bg-[#C79A3B] hover:text-white transition-colors">?</button>
      {open && (
        <span className="absolute z-40 top-7 right-0 w-64 bg-[#12263A] text-white text-xs leading-relaxed p-3 rounded shadow-xl normal-case tracking-normal font-normal" data-testid={testid ? `${testid}-content` : undefined}>{text}</span>
      )}
    </span>
  );
}

export function YesNo({ label, value, onChange, testid, info }) {
  return (
    <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4">
      <div className="text-sm md:text-base pr-4 flex items-center gap-2">
        <span>{label}</span>
        {info && <InfoTip text={info} testid={`${testid}-info`} />}
      </div>
      <div className="flex gap-2">
        {["yes", "no"].map(v => (
          <button key={v} data-testid={`${testid}-${v}`} onClick={()=>onChange(v)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${value===v?"bg-[#12263A] text-white border-[#12263A]":"border-[#E5E7EB] bg-white"}`}>{v}</button>
        ))}
      </div>
    </div>
  );
}

export function QuoteList({ title, items, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="mb-4" data-testid={testid}>
      <div className="label-eyebrow mb-2">{title}</div>
      <ul className="text-sm text-[#6B7280] space-y-1 list-disc pl-4">
        {items.map((it, i) => <li key={`${i}-${it}`}>{it}</li>)}
      </ul>
    </div>
  );
}

export function Group({ label, children }) {
  return (
    <div className="mb-6">
      <div className="label-eyebrow mb-3">{label}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function Slider({ label, min, max, step, value, onChange, testid }) {
  return (
    <div>
      <div className="text-sm font-medium mb-1">{label}</div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))} className="w-full de-range"/>
      <div className="text-[10px] text-[#6B7280] flex justify-between"><span>{min}</span><span>{max}</span></div>
    </div>
  );
}

export function Choice({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([v,l])=>(
        <button key={v} data-testid={`${testid}-${v}`} onClick={()=>onChange(v)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${value===v?"bg-[#12263A] text-white border-[#12263A]":"border-[#E5E7EB] bg-white"}`}>{l}</button>
      ))}
    </div>
  );
}

export function Toggle({ label, value, onChange, testid, disabled }) {
  return (
    <button disabled={disabled} data-testid={testid} onClick={()=>!disabled&&onChange(!value)} className={`w-full text-left flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm ${disabled?"opacity-40":""}`}>
      <span>{label}</span>
      <span className={`w-5 h-5 border ${value?"bg-[#C79A3B] border-[#C79A3B]":"border-[#12263A] bg-white"} flex items-center justify-center`}>{value && <Check size={12} className="text-white"/>}</span>
    </button>
  );
}

export function Counter({ label, value, onChange, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm">
      <span>{label}</span>
      <div className="flex items-center gap-2">
        <button data-testid={`${testid}-dec`} onClick={()=>onChange(Math.max(0,value-1))} aria-label="Decrease" className="w-9 h-9 sm:w-7 sm:h-7 border border-[#E5E7EB] text-base sm:text-sm active:bg-[#F0EAE0]">–</button>
        <span className="w-6 text-center font-bold">{value}</span>
        <button data-testid={`${testid}-inc`} onClick={()=>onChange(value+1)} aria-label="Increase" className="w-9 h-9 sm:w-7 sm:h-7 border border-[#E5E7EB] text-base sm:text-sm active:bg-[#F0EAE0]">+</button>
      </div>
    </div>
  );
}

export function StepTracker({ phaseIndex }) {
  return (
    <div data-testid="step-tracker" className="px-6 md:px-12 lg:px-16 pt-10 pb-2">
      <div className="flex items-center">
        {PHASES.map((label, i) => {
          const done = i < phaseIndex, active = i === phaseIndex;
          return (
            <div key={label} className={`flex items-center ${i < PHASES.length-1 ? "flex-1" : ""}`}>
              <div className="flex items-center gap-2 md:gap-3 shrink-0" data-testid={`step-phase-${i}`}>
                <div className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center text-sm font-black border-2 transition-colors
                  ${active ? "bg-[#C79A3B] border-[#C79A3B] text-white" : done ? "bg-[#12263A] border-[#12263A] text-white" : "bg-white border-[#E5E7EB] text-[#6B7280]"}`}>
                  {done ? <Check size={16}/> : i + 1}
                </div>
                <div className={`hidden md:block text-xs font-bold uppercase tracking-wider ${active ? "text-[#C79A3B]" : done ? "text-[#12263A]" : "text-[#6B7280]"}`}>{label}</div>
              </div>
              {i < PHASES.length - 1 && <div className={`h-[2px] flex-1 mx-2 md:mx-3 ${i < phaseIndex ? "bg-[#12263A]" : "bg-[#E5E7EB]"}`}/>}
            </div>
          );
        })}
      </div>
      <div className="md:hidden mt-3 text-xs font-bold uppercase tracking-wider text-[#C79A3B]" data-testid="step-current-label">{phaseIndex+1}. {PHASES[phaseIndex]}</div>
    </div>
  );
}

export function IconCard({ icon: Icon, img, label, desc, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex flex-col items-center justify-start gap-2 border-2 p-3 transition-colors text-center
        ${active ? "border-[#C79A3B] bg-[#F7E6DD]" : "border-[#E5E7EB] bg-white hover:border-[#12263A]"}`}>
      {img ? (
        <img src={img} alt={label} loading="lazy" className="w-full aspect-square object-cover rounded-sm" />
      ) : (
        <div className="w-14 h-14 md:w-16 md:h-16">{Icon ? <Icon/> : null}</div>
      )}
      <span className={`text-[11px] font-bold uppercase tracking-wider leading-tight ${active?"text-[#C79A3B]":"text-[#12263A]"}`}>{label}</span>
      {desc && <span className="text-[10px] font-normal normal-case tracking-normal leading-snug text-[#6B7280]">{desc}</span>}
    </button>
  );
}

export function ColourGrid({ value, onChange, options, testid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map(o => {
        const active = value === o.key;
        return (
          <button key={o.key} data-testid={`${testid}-${o.key}`} onClick={()=>onChange(o.key)}
            className={`flex items-center gap-2 border-2 p-2 text-left transition-colors ${active?"border-[#C79A3B] bg-[#F7E6DD]":"border-[#E5E7EB] bg-white hover:border-[#12263A]"}`}>
            <span className="w-7 h-7 rounded-full border border-black/15 shrink-0" style={{ background: o.hex }}/>
            <span className="min-w-0 flex-1">
              <span className={`block text-[11px] font-bold leading-tight truncate ${active?"text-[#C79A3B]":"text-[#12263A]"}`}>{o.label}</span>
              {o.sub && <span className="block text-[10px] text-[#6B7280] leading-tight">{o.sub}</span>}
            </span>
            {active && <Check size={14} className="text-[#C79A3B] shrink-0"/>}
          </button>
        );
      })}
    </div>
  );
}

export function BrickGrid({ value, onChange, options, testid }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
      {options.map(o => {
        const active = value === o.key;
        return (
          <button key={o.key} data-testid={`${testid}-${o.key}`} onClick={()=>onChange(o.key)}
            className={`border-2 p-1.5 transition-colors ${active?"border-[#C79A3B]":"border-[#E5E7EB] hover:border-[#12263A]"}`}>
            <span className="block h-10 w-full mb-1" style={{
              backgroundColor: o.brick,
              backgroundImage: `repeating-linear-gradient(0deg, ${o.mortar} 0 1.5px, transparent 1.5px 11px), repeating-linear-gradient(90deg, ${o.mortar} 0 1.5px, transparent 1.5px 24px)`,
            }}/>
            <span className={`block text-[10px] font-bold uppercase tracking-wider text-center leading-tight ${active?"text-[#C79A3B]":"text-[#12263A]"}`}>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Field({ label, v, on, type="text", testid }) {
  return (
    <div className="mb-4">
      <label className="label-eyebrow block mb-2">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={e=>on(e.target.value)} className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]"/>
    </div>
  );
}

export const KT_OPTIONS = [
  { key: "keep", title: "Keep existing doorway", desc: "Standard option using your current rear door opening.", img: "/knockthrough/knockthrough-1.png", node: "Door opening only", Icon: KnockDoor },
  { key: "wider", title: "Create a wider opening", desc: "Open up part of the rear wall with structural support.", img: "/knockthrough/knockthrough-2.png", node: "Partial opening", Icon: KnockPartial },
  { key: "full", title: "Full knock-through", desc: "Remove most of the rear wall to create one open-plan space.", img: "/knockthrough/knockthrough-3.png", node: "Full-width knock-through", Icon: KnockFull },
];

export function KtRadio({ active }) {
  return (
    <span className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${active ? "border-[#C79A3B]" : "border-[#6B7280]"}`}>
      {active && <span className="w-2 h-2 rounded-full bg-[#C79A3B]" />}
    </span>
  );
}

export function KnockThrough({ value, onChange }) {
  const val = value === true ? "full" : (value || "keep");
  return (
    <div className="space-y-5" data-testid="cfg-knockthrough">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {KT_OPTIONS.map(o => {
          const active = val === o.key;
          return (
            <button key={o.key} type="button" data-testid={`kt-card-${o.key}`} onClick={() => onChange(o.key)}
              className={`text-left rounded-2xl border-2 overflow-hidden transition-colors ${active ? "border-[#C79A3B] bg-[#F7E6DD]" : "border-[#E5E7EB] bg-white hover:border-[#6B7280]"}`}>
              <div className="p-3.5">
                <div className="flex items-start gap-2.5">
                  <span className="mt-0.5"><KtRadio active={active} /></span>
                  <div>
                    <div className={`text-[12px] font-bold uppercase tracking-wider leading-tight ${active ? "text-[#C79A3B]" : "text-[#12263A]"}`}>{o.title}</div>
                    <div className="text-[11px] text-[#6B7280] mt-1 leading-snug">{o.desc}</div>
                  </div>
                </div>
              </div>
              <div className="px-2 pb-2">
                <img src={o.img} alt={o.title} loading="lazy" className="w-full h-28 md:h-32 object-cover rounded-xl" />
              </div>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-[#E5E7EB] bg-[#F4F4F4] p-5">
        <div className="label-eyebrow mb-5 text-center">How open would you like the connection to be?</div>
        <div className="relative flex items-start justify-between">
          <div className="absolute top-7 left-[16%] right-[16%] h-[2px] bg-[#E5E7EB]" />
          {KT_OPTIONS.map(o => {
            const active = val === o.key;
            const Icon = o.Icon;
            return (
              <button key={o.key} type="button" data-testid={`kt-node-${o.key}`} onClick={() => onChange(o.key)}
                className="relative z-10 flex-1 flex flex-col items-center gap-2 px-1">
                <span className={`w-14 h-14 rounded-full border-2 flex items-center justify-center bg-white transition-colors ${active ? "border-[#C79A3B]" : "border-[#E5E7EB]"}`}>
                  <span className="w-8 h-8"><Icon active={active} /></span>
                </span>
                <KtRadio active={active} />
                <span className={`text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-center leading-tight ${active ? "text-[#C79A3B]" : "text-[#6B7280]"}`}>{o.node}</span>
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#6B7280] text-center mt-5">Final beam size is confirmed by our structural calculations at survey.</p>
      </div>
    </div>
  );
}
